import axios from 'axios';
import authManager from './utils/authManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost';

// Validar que la URL tenga protocolo
const getBaseURL = () => {
  if (!API_URL) {
    console.error('REACT_APP_BACKEND_URL no está definido');
    return 'http://localhost/api/v1';
  }
  
  // Si no tiene protocolo, agregar http://
  if (!API_URL.startsWith('http://') && !API_URL.startsWith('https://')) {
    return `http://${API_URL}/api/v1`;
  }
  
  return `${API_URL}/api/v1`;
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000, // 10 segundos de timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('consulterest_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log error para debugging (solo errores de conexión)
    if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error')) {
      console.error('Error de conexión:', error.message);
      console.error('URL intentada:', error.config?.url);
      console.error('Base URL:', api.defaults.baseURL);
    }
    
    // Manejar errores de autenticación (401 Unauthenticated, 419 CSRF Token Mismatch)
    const status = error?.response?.status;
    if (status === 401 || status === 419) {
      // El AuthManager maneja todo: limpieza de tokens, callbacks y redirección
      authManager.handleAuthError(error);
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const login = (identifier, password, type = 'email') => {
  const payload = { password };
  if (type === 'email') {
    payload.email = identifier;
  } else {
    payload.phone = identifier;
  }
  return api.post('/auth/login', payload);
};
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Patients
export const getPatients = (search) => api.get('/patients', { params: { search } });
export const getPatient = (id) => api.get(`/patients/${id}`);
export const createPatient = (data) => api.post('/patients', data);
export const updatePatient = (id, data) => api.put(`/patients/${id}`, data);
export const deletePatient = (id) => api.delete(`/patients/${id}`);

// Appointments
export const getAppointments = (params) => api.get('/appointments', { params });
export const getAppointment = (id) => api.get(`/appointments/${id}`);
export const createAppointment = (data) => api.post('/appointments', data);
export const updateAppointment = (id, data) => api.put(`/appointments/${id}`, data);
export const deleteAppointment = (id) => api.delete(`/appointments/${id}`);
export const cancelAppointment = (id, cancellationReason) => api.post(`/appointments/${id}/cancel`, { cancellation_reason: cancellationReason });
export const getRescheduleQueue = () => api.get('/appointments/reschedule-queue');
export const updateAppointmentAttendance = (id, status) => api.patch(`/appointments/${id}/attendance`, { status });
export const getAppointmentPayments = (id) => api.get(`/appointments/${id}/payments`);
export const createAppointmentPayment = (id, data) => api.post(`/appointments/${id}/payments`, data);
export const deleteAppointmentPayment = (appointmentId, paymentId) => api.delete(`/appointments/${appointmentId}/payments/${paymentId}`);
export const getPaymentTypes = () => api.get('/payment-types');

// Availability Blocks
export const getAvailabilityBlocks = (params) => api.get('/availability-blocks', { params });
export const createAvailabilityBlock = (data) => api.post('/availability-blocks', data);
export const deleteAvailabilityBlock = (id) => api.delete(`/availability-blocks/${id}`);

// Clinical History
export const getClinicalHistory = (patientId) => api.get(`/patients/${patientId}/clinical-history`);
export const createClinicalHistory = (patientId, data) => api.post(`/patients/${patientId}/clinical-history`, data);
export const updateClinicalHistory = (patientId, data) => api.put(`/patients/${patientId}/clinical-history`, data);
export const closeClinicalHistory = (patientId) => api.post(`/patients/${patientId}/clinical-history/close`);
export const unlockClinicalHistory = (patientId, reason) => api.post(`/patients/${patientId}/clinical-history/unlock`, null, { params: { reason } });

// SOAP Notes
export const getSOAPNotes = (patientId) => api.get(`/patients/${patientId}/soap-notes`);
export const getSOAPNote = (id) => api.get(`/soap-notes/${id}`);
export const createSOAPNote = (patientId, data, appointmentId) => api.post(`/patients/${patientId}/soap-notes`, data, { params: { appointment_id: appointmentId } });
export const updateSOAPNote = (id, data) => api.put(`/soap-notes/${id}`, data);
export const deleteSOAPNote = (id) => api.delete(`/soap-notes/${id}`);
export const signSOAPNote = (id) => api.post(`/soap-notes/${id}/sign`);
export const addSOAPAddendum = (id, content) => api.post(`/soap-notes/${id}/addendum`, { content });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSetting = (key, value) => api.put(`/settings/${key}`, { value });

// Reports
export const getAppointmentStats = (params) => api.get('/reports/appointments-stats', { params });
export const getPendingSOAP = () => api.get('/reports/pending-soap');
export const exportExcel = (params) => api.get('/reports/export/excel', { params, responseType: 'blob' });
export const exportPDF = (params) => api.get('/reports/export/pdf', { params, responseType: 'blob' });

// Notification Logs
export const getNotificationLogs = (patientId) => api.get('/notification-logs', { params: { patient_id: patientId } });

// Seed
export const seedDatabase = () => api.post('/seed');

// ==================== PATIENT PORTAL (auth + role PACIENTE) ====================
// Solo para usuario con rol PACIENTE. Devuelve solo sus datos.
export const getMyProfile = () => api.get('/me/profile');
export const updateMyProfile = (data) => api.put('/me/profile', data);
export const getMyAppointments = (params) => api.get('/me/appointments', { params });
export const getMyAppointment = (id) => api.get(`/me/appointments/${id}`);

// ==================== PUBLIC ENDPOINTS (NO AUTH) ====================
const getPublicBaseURL = () => {
  if (!API_URL) {
    return 'http://localhost/api/v1/public';
  }
  
  if (!API_URL.startsWith('http://') && !API_URL.startsWith('https://')) {
    return `http://${API_URL}/api/v1/public`;
  }
  
  return `${API_URL}/api/v1/public`;
};

const publicApi = axios.create({
  baseURL: getPublicBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000,
});

// Public booking
export const getAvailableSlots = (date) => publicApi.get('/available-slots', { params: { target_date: date } });
export const publicBookAppointment = (data) => {
  // Asegurar que phone esté presente (no whatsapp)
  const payload = { ...data };
  if (payload.whatsapp) {
    payload.phone = payload.whatsapp;
    delete payload.whatsapp;
  }
  // Eliminar consent_notifications si viene (no se envía al backend)
  delete payload.consent_notifications;
  return publicApi.post('/book', payload);
};

// Patient portal
export const requestPatientAccess = (whatsapp) => publicApi.post('/patient-access', { whatsapp });
export const getPatientPortal = (token) => publicApi.get('/patient-portal', { params: { token } });
export const patientCancelAppointment = (appointmentId, token) => publicApi.post(`/patient-portal/cancel/${appointmentId}`, null, { params: { token } });
export const patientRescheduleAppointment = (appointmentId, token, newDate, newStartTime, newEndTime) => 
  publicApi.post(`/patient-portal/reschedule/${appointmentId}`, null, { 
    params: { token, new_date: newDate, new_start_time: newStartTime, new_end_time: newEndTime } 
  });

export default api;
